import { useState, useMemo, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AppLayout } from '@/components/layout/AppLayout'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { useRegion } from '@/hooks/useRegion'
import { useNation } from '@/hooks/useNation'
import { useWorld } from '@/hooks/useWorld'
import { createDistrict } from '@/services/districts'
import type { BreadcrumbItem } from '@/components/layout/Breadcrumb'
import type { SettlementType } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export default function DistrictCreate() {
  const { regionId } = useParams<{ regionId: string }>()
  const navigate = useNavigate()
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin()
  const { region } = useRegion(regionId)
  const { nation } = useNation(region?.nation_id)
  const { world } = useWorld(nation?.world_id)

  // Form fields
  const [name, setName] = useState('')
  const [settlementType, setSettlementType] = useState<SettlementType>('rural')
  const [population, setPopulation] = useState('0')
  const [seatCount, setSeatCount] = useState('0')

  // Form state
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [{ label: 'Worlds', href: '/worlds' }]
    if (world) {
      items.push({ label: world.name, href: `/worlds/${world.id}` })
    }
    if (nation) {
      items.push({ label: nation.name, href: `/nations/${nation.id}` })
    }
    if (region) {
      items.push({ label: region.name, href: `/regions/${region.id}` })
    }
    items.push({ label: 'Create District' })
    return items
  }, [world, nation, region])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!name.trim()) {
      setError('Name is required')
      return
    }

    const pop = Number(population)
    if (isNaN(pop) || pop < 0) {
      setError('Population must be a non-negative number')
      return
    }

    const seats = Number(seatCount)
    if (isNaN(seats) || seats < 0) {
      setError('Seat count must be a non-negative number')
      return
    }

    if (!regionId) {
      setError('Region ID is missing')
      return
    }

    setIsSubmitting(true)

    try {
      await createDistrict({
        region_id: regionId,
        name: name.trim(),
        settlement_type: settlementType,
        population: pop,
        seat_count: seats,
      })
      navigate(`/regions/${regionId}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create district')
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate(`/regions/${regionId}`)
  }

  // Show loading while checking admin status
  if (isAdminLoading) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <div className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </AppLayout>
    )
  }

  // Redirect non-admins
  if (!isAdmin) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <Card className="mx-auto max-w-md">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>Only administrators can create new districts.</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button variant="outline" onClick={() => navigate(`/regions/${regionId}`)}>
              Back to Region
            </Button>
          </CardFooter>
        </Card>
      </AppLayout>
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Card className="mx-auto max-w-2xl">
        <CardHeader>
          <CardTitle>Create New District</CardTitle>
          <CardDescription>Add a new district to this region.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6">
            {error && (
              <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Enter district name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="settlementType">Settlement Type</Label>
              <Select
                value={settlementType}
                onValueChange={(value: string) => setSettlementType(value as SettlementType)}
                disabled={isSubmitting}
              >
                <SelectTrigger id="settlementType">
                  <SelectValue placeholder="Select settlement type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rural">Rural</SelectItem>
                  <SelectItem value="suburban">Suburban</SelectItem>
                  <SelectItem value="urban">Urban</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">
                The type of settlement in this district.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="population">Population</Label>
              <Input
                id="population"
                type="number"
                min="0"
                placeholder="0"
                value={population}
                onChange={(e) => setPopulation(e.target.value)}
                disabled={isSubmitting}
              />
              <p className="text-muted-foreground text-xs">Total population of this district.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="seatCount">Seat Count</Label>
              <Input
                id="seatCount"
                type="number"
                min="0"
                placeholder="0"
                value={seatCount}
                onChange={(e) => setSeatCount(e.target.value)}
                disabled={isSubmitting}
              />
              <p className="text-muted-foreground text-xs">
                Number of legislative seats allocated to this district.
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex gap-3">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create District'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </AppLayout>
  )
}
